import { useEffect, useRef, useState } from "react";
import { api, errMsg, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, FolderOpen, MessageSquare, Phone, PhoneOff, Save, Search, Settings, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const REC = "http://127.0.0.1:5050";
const DIAS = ["domingo", "segunda-feira", "terça-feira", "quarta-feira", "quinta-feira", "sexta-feira", "sábado"];
const MESES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

function saudacaoPadrao(tenant) {
  const h = new Date().getHours();
  const cumprimento = h >= 6 && h < 12 ? "Bom dia" : h < 18 ? "Boa tarde" : "Boa noite";
  return `${cumprimento}, essa é uma ligação da portaria principal do ${tenant || "condomínio"}. Em conformidade à Lei 13.709, informamos que esta ligação poderá ser gravada. Ao atender, você estará concordando com nossa política de privacidade.`;
}

function gerarProtocolo() {
  const d = new Date();
  const p = (n, l = 2) => String(n).padStart(l, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}${p(Math.floor(Math.random() * 1000), 3)}`;
}

function fmtUptime(seg) {
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function PortariaTelefonePage() {
  const [agora, setAgora] = useState(new Date());
  const [eql, setEql] = useState("");
  const [tenant, setTenant] = useState("");
  const [moradores, setMoradores] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [saudacao, setSaudacao] = useState(() => localStorage.getItem("portaria-saudacao") || "");
  const [saudacaoAberta, setSaudacaoAberta] = useState(false);
  const [configAberta, setConfigAberta] = useState(false);
  const [driverAberto, setDriverAberto] = useState(false);
  const [chamada, setChamada] = useState(null);
  const [gravando, setGravando] = useState(false);
  const [decorridos, setDecorridos] = useState(0);
  const [resultado, setResultado] = useState(null);
  const [recOnline, setRecOnline] = useState(false);
  const [modo, setModo] = useState("cable");
  const [devices, setDevices] = useState({ entradas: [], saidas_loopback: [] });
  const [device, setDevice] = useState("");
  const [mic, setMic] = useState("");
  const [saude, setSaude] = useState(null);
  const [encerrando, setEncerrando] = useState(false);
  const [gravModal, setGravModal] = useState(false);
  const [gravLista, setGravLista] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setAgora(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    fetch(`${REC}/api/devices`).then((r) => r.json()).then((d) => {
      setRecOnline(true);
      setDevices(d);
      const cabo = d.entradas.find((x) => /cable output/i.test(x.nome));
      const padraoSaida = d.saidas_loopback.find((x) => x.index === d.padrao_saida);
      if (cabo) { setModo("cable"); setDevice(String(cabo.index)); }
      else if (padraoSaida) { setModo("loopback"); setDevice(String(padraoSaida.index)); }
      const micPadrao = d.entradas.find((x) => x.index === d.padrao_entrada && !/cable/i.test(x.nome));
      if (micPadrao) setMic(String(micPadrao.index));
    }).catch(() => setRecOnline(false));
    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    const ping = () => fetch(`${REC}/api/health`).then((r) => r.json()).then(setSaude).catch(() => setSaude(null));
    ping();
    const t = setInterval(ping, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!resultado) return;
    const t = setTimeout(() => { setResultado(null); setMoradores(null); setEql(""); }, 5000);
    return () => clearTimeout(t);
  }, [resultado]);

  useEffect(() => {
    if (!gravando) return;
    pollRef.current = setInterval(async () => {
      try {
        const s = await (await fetch(`${REC}/api/status`)).json();
        setDecorridos(s.decorridos || 0);
      } catch {}
    }, 1000);
    return () => clearInterval(pollRef.current);
  }, [gravando]);

  const saudacaoEfetiva = saudacao || saudacaoPadrao(tenant);

  async function buscar() {
    if (!eql.trim()) return toast.error("Informe a EQL da unidade");
    setBuscando(true);
    setMoradores(null);
    try {
      const { data } = await api.get("/tenant/portaria/moradores", { params: { eql: eql.trim() } });
      if (!data.ok) return toast.error(data.motivo === "integracao_indisponivel" ? "Integração Brave indisponível" : "Falha ao buscar moradores");
      setTenant(data.tenant || "");
      setMoradores(data.moradores || []);
      if (!(data.moradores || []).length) toast.info("Nenhum morador com telefone nesta unidade");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBuscando(false);
    }
  }

  function salvarSaudacao() {
    localStorage.setItem("portaria-saudacao", saudacao || saudacaoPadrao(tenant));
    setSaudacaoAberta(false);
    toast.success("Saudação salva neste computador");
  }

  function limparBusca() {
    setEql("");
    setMoradores(null);
  }

  function abrirWhatsApp(fone, texto) {
    // Protocolo direto: abre o WhatsApp Desktop sem criar janela de navegador.
    // (wa.me redefine o nome da janela e se auto-fecha — reuso por popup nao funciona)
    const ifr = document.createElement("iframe");
    ifr.style.display = "none";
    ifr.src = `whatsapp://send?phone=${fone}&text=${encodeURIComponent(texto)}`;
    document.body.appendChild(ifr);
    setTimeout(() => ifr.remove(), 5000);
  }

  function cumprimentoAtual() {
    const h = new Date().getHours();
    return h >= 6 && h < 12 ? "Bom dia" : h < 18 ? "Boa tarde" : "Boa noite";
  }

  function enviarMensagem(m) {
    const texto = `${cumprimentoAtual()}, ${m.nome}! Unidade ${eql.trim().toUpperCase()}.`;
    abrirWhatsApp(`${m.ddi}${m.celular}`, texto);
    toast.info(`Abrindo WhatsApp para mensagem a ${m.nome}…`, { duration: 3000 });
  }

  function ligar(m) {
    const fone = `${m.ddi}${m.celular}`;
    const protocolo = gerarProtocolo();
    setResultado(null);
    toast.info(`Chamando ${m.nome}… Protocolo ${protocolo}`, { duration: 6000, id: "chamando" });
    abrirWhatsApp(fone, `${saudacaoEfetiva}\nProtocolo desta ligação: ${protocolo}`);
    const alvo = { eql: eql.trim().toUpperCase(), nome: m.nome, telefone: m.telefone, protocolo };
    setChamada(alvo);
    setTimeout(() => iniciarGravacao(alvo), 3000);
  }

  async function iniciarGravacao(alvo) {
    try {
      const r = await fetch(`${REC}/api/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          modo, device, mic_device: mic,
          protocolo: alvo?.protocolo || "", eql: alvo?.eql || "", nome: alvo?.nome || "",
        }),
      });
      const j = await r.json();
      if (j.erro) throw new Error(j.erro);
      setChamada(alvo);
      setGravando(true);
    } catch (e) {
      toast.error(`Gravação não iniciada: ${e.message || "gravador local (127.0.0.1:5050) offline — abra o RecordWeb no PC"}`);
    }
  }

  async function listarGravacoes() {
    setGravModal(true);
    setGravLista(null);
    try {
      const { data } = await api.get("/tenant/portaria/gravacoes", { params: { eql: eql.trim() } });
      if (!data.ok) {
        setGravLista([]);
        toast.error(data.motivo === "nao_configurado"
          ? "Servidor de gravações não configurado para este condomínio"
          : "Servidor de gravações indisponível");
        return;
      }
      setGravLista(data.gravacoes || []);
    } catch (e) {
      setGravLista([]);
      toast.error(errMsg(e));
    }
  }

  async function encerrarGravacao() {
    if (encerrando) return;
    setEncerrando(true);
    try {
      const j = await (await fetch(`${REC}/api/stop`, { method: "POST" })).json();
      if (j.erro) throw new Error(j.erro);
      setResultado(j);
      toast.success(`Gravação salva (${j.duracao_seg}s)`);
      api.post("/tenant/portaria/registrar", {
        eql: chamada?.eql || "", nome: chamada?.nome || "", telefone: chamada?.telefone || "",
        protocolo: j.protocolo || chamada?.protocolo || "",
        duracao_seg: j.duracao_seg || 0, upload: j.upload || "",
      }).catch(() => {});
    } catch (e) {
      toast.error(`Falha ao encerrar: ${e.message}`);
    } finally {
      setGravando(false);
      setEncerrando(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="portaria-telefone-page">
      <section data-testid="portaria-secao-relogio"
        className="relative bg-black text-white border-4 border-zinc-700 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.6)] py-8 text-center">
        <div className="text-6xl font-mono font-bold tracking-widest" data-testid="portaria-relogio">
          {agora.toLocaleTimeString("pt-BR")}
        </div>
        <div className="mt-3 text-lg text-zinc-300 capitalize" data-testid="portaria-data">
          {DIAS[agora.getDay()]}, {agora.getDate()} de {MESES[agora.getMonth()]} de {agora.getFullYear()}
        </div>
        <button type="button" onClick={() => setConfigAberta(true)} title="Configurações"
          className="absolute bottom-3 right-3 text-zinc-500 hover:text-white transition-colors"
          data-testid="portaria-config-btn">
          <Settings className="h-5 w-5" />
        </button>
      </section>

      <section data-testid="portaria-secao-chamada" className="bg-slate-100 dark:bg-zinc-800/60 rounded-2xl p-5 space-y-4 border border-slate-200 dark:border-zinc-700">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-zinc-600 dark:text-zinc-300">Chamada WhatsApp</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={listarGravacoes} data-testid="portaria-gravacoes-btn">
              <FolderOpen className="h-3.5 w-3.5 mr-1" /> Gravações
            </Button>
            <span data-testid="portaria-health-badge"
              className={`text-xs px-2 py-1 rounded-full ${saude ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300"}`}>
              {saude ? `● Gravador no ar há ${fmtUptime(saude.uptime_seg)}` : "● Gravador offline"}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input value={eql} onChange={(e) => setEql(e.target.value)} placeholder="EQL da unidade (ex.: 101)"
              className="bg-slate-200 text-black placeholder:text-zinc-500 border-slate-300 pr-8 uppercase"
              data-testid="portaria-eql-input" onKeyDown={(e) => e.key === "Enter" && buscar()} />
            {eql && (
              <button type="button" onClick={limparBusca} title="Limpar"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-black transition-colors"
                data-testid="portaria-eql-limpar">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button onClick={buscar} disabled={buscando} data-testid="portaria-buscar-btn">
            <Search className="h-4 w-4 mr-1" /> {buscando ? "Buscando…" : "Buscar"}
          </Button>
        </div>

        {moradores && (
          <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-zinc-700" data-testid="portaria-moradores-lista">
            <table className="w-full text-xs">
              <thead className="bg-slate-200 dark:bg-zinc-900/60 text-left">
                <tr><th className="px-2 py-1">Nome</th><th className="px-2 py-1">Tipo</th><th className="px-2 py-1">Telefone</th><th className="px-2 py-1"></th></tr>
              </thead>
              <tbody>
                {moradores.map((m, i) => (
                  <tr key={i} className="border-t border-slate-200 dark:border-zinc-700" data-testid={`portaria-morador-${m.celular}`}>
                    <td className="px-2 py-1">{m.nome}</td>
                    <td className="px-2 py-1 text-zinc-500">{m.tipo}</td>
                    <td className="px-2 py-1 font-mono whitespace-nowrap">{m.telefone}</td>
                    <td className="px-2 py-1 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => enviarMensagem(m)}
                        title="Enviar mensagem no WhatsApp (sem ligar)"
                        className="mr-1 h-7 px-2" data-testid={`portaria-msg-${m.celular}`}>
                        <MessageSquare className="h-3.5 w-3.5" />
                      </Button>
                      {saude && (
                        <Button size="sm" onClick={() => ligar(m)} disabled={gravando}
                          title="Ligar com gravação (gravador local conectado)"
                          className="h-7 px-2" data-testid={`portaria-ligar-${m.celular}`}>
                          <Phone className="h-3.5 w-3.5 mr-1" /> Ligar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {!moradores.length && <tr><td colSpan={4} className="px-3 py-4 text-center text-zinc-500">Nenhum morador com telefone</td></tr>}
              </tbody>
            </table>
          </div>
        )}

      </section>

      <section data-testid="portaria-secao-gravacao" className="bg-slate-100 dark:bg-zinc-800/60 rounded-2xl p-5 border border-slate-200 dark:border-zinc-700">
        <p className="text-sm text-zinc-500 text-center" data-testid="portaria-gravacao-vazia">
          Ao clicar em Ligar, o WhatsApp abre com o contato e a saudação prontos — a gravação inicia automaticamente após 3 segundos e os dados da chamada aparecem em uma janela central.
        </p>
      </section>

      <Dialog open={!!((gravando && chamada) || resultado)}
        onOpenChange={(aberto) => { if (!aberto && !gravando) setResultado(null); }}>
        <DialogContent data-testid="portaria-gravacao-modal" className="sm:max-w-md"
          onInteractOutside={(e) => gravando && e.preventDefault()}
          onEscapeKeyDown={(e) => gravando && e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{gravando ? "Gravação em andamento" : "Gravação concluída"}</DialogTitle>
          </DialogHeader>
          {gravando && chamada ? (
            <div className="space-y-3" data-testid="portaria-gravacao-ativa">
              <div className="flex items-center gap-2 text-rose-500 font-semibold">
                <span className="animate-pulse">●</span> Gravando há {Math.floor(decorridos)}s
              </div>
              <div className="text-sm space-y-1">
                <div><span className="text-zinc-500">Unidade:</span> <span data-testid="portaria-grav-eql">{chamada.eql}</span></div>
                <div><span className="text-zinc-500">Morador:</span> <span data-testid="portaria-grav-nome">{chamada.nome}</span></div>
                <div><span className="text-zinc-500">Telefone:</span> <span data-testid="portaria-grav-fone">{chamada.telefone}</span></div>
                <div><span className="text-zinc-500">Protocolo:</span> <span className="font-mono" data-testid="portaria-grav-protocolo">{chamada.protocolo}</span></div>
              </div>
              <Button variant="destructive" onClick={encerrarGravacao} disabled={encerrando} className="w-full" data-testid="portaria-encerrar-btn">
                <PhoneOff className="h-4 w-4 mr-2" /> {encerrando ? "Encerrando…" : "Encerrar gravação"}
              </Button>
            </div>
          ) : resultado ? (
            <div className="space-y-2" data-testid="portaria-gravacao-resultado">
              <p className="text-sm font-semibold text-emerald-500">Gravação salva ({resultado.duracao_seg}s):</p>
              <ul className="text-sm font-mono text-zinc-600 dark:text-zinc-300">
                {resultado.arquivos.map((a) => <li key={a}>{a}</li>)}
              </ul>
              <p className="text-xs text-zinc-500" data-testid="portaria-upload-status">
                Protocolo <span className="font-mono">{resultado.protocolo || "—"}</span> · Envio para a nuvem: {resultado.upload || "—"}
              </p>
              <Button variant="outline" size="sm" onClick={() => { setResultado(null); setMoradores(null); setEql(""); }} data-testid="portaria-nova-chamada-btn">Nova chamada</Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={gravModal} onOpenChange={setGravModal}>
        <DialogContent className="sm:max-w-lg" data-testid="portaria-gravacoes-modal">
          <DialogHeader>
            <DialogTitle>Gravações {eql.trim() ? `— EQL ${eql.trim().toUpperCase()}` : "(todas)"}</DialogTitle>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto pr-1">
            {gravLista === null ? (
              <p className="text-sm text-zinc-500">Carregando…</p>
            ) : gravLista.length === 0 ? (
              <p className="text-sm text-zinc-500" data-testid="portaria-gravacoes-vazio">Nenhuma gravação encontrada.</p>
            ) : (
              <ul className="space-y-3">
                {gravLista.map((g) => (
                  <li key={g.arquivo} className="text-xs" data-testid={`gravacao-${g.arquivo}`}>
                    <div className="font-mono text-zinc-700 dark:text-zinc-300 break-all">{g.arquivo}</div>
                    <div className="text-zinc-500 mb-1">
                      {(g.bytes / 1024).toFixed(0)} KB{g.modificado ? ` · ${new Date(g.modificado).toLocaleString("pt-BR")}` : ""}
                    </div>
                    <audio controls preload="none" className="w-full h-8"
                      src={`${API}/tenant/portaria/gravacoes/arquivo?nome=${encodeURIComponent(g.arquivo)}`} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={configAberta} onOpenChange={setConfigAberta}>
        <DialogContent data-testid="portaria-config-modal" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Configurações da portaria</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-xl border border-slate-200 dark:border-zinc-700" data-testid="portaria-saudacao-card">
              <button type="button" data-testid="portaria-saudacao-toggle"
                onClick={() => {
                  const abrir = !saudacaoAberta;
                  setSaudacaoAberta(abrir);
                  if (abrir && !saudacao) setSaudacao(saudacaoPadrao(tenant));
                }}
                className="w-full flex items-center justify-between px-4 py-3 text-left">
                <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Mensagem de saudação (enviada antes da chamada)</span>
                {saudacaoAberta ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
              </button>
              {saudacaoAberta && (
                <div className="px-4 pb-4 space-y-2">
                  <textarea value={saudacao} onChange={(e) => setSaudacao(e.target.value)} rows={4}
                    className="w-full text-sm rounded-md border border-slate-300 dark:border-zinc-600 bg-transparent p-2"
                    data-testid="portaria-saudacao-textarea" />
                  <Button size="sm" onClick={salvarSaudacao} data-testid="portaria-saudacao-salvar">
                    <Save className="h-4 w-4 mr-1" /> Salvar
                  </Button>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 dark:border-zinc-700" data-testid="portaria-driver-card">
              <button type="button" data-testid="portaria-driver-toggle" onClick={() => setDriverAberto(!driverAberto)}
                className="w-full flex items-center justify-between px-4 py-3 text-left">
                <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Driver de Gravação</span>
                {driverAberto ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
              </button>
              {driverAberto && (
                <div className="px-4 pb-4 space-y-3">
                  <div className={`text-xs ${recOnline ? "text-emerald-500" : "text-rose-400"}`} data-testid="portaria-rec-status">
                    {recOnline ? "● Gravador local conectado" : "● Gravador local offline (abra o RecordWeb no PC)"}
                  </div>
                  {recOnline && (
                    <>
                      <label className="block text-xs text-zinc-500">Modo de captura
                        <select value={modo} onChange={(e) => setModo(e.target.value)}
                          className="mt-1 w-full text-sm rounded-md border border-slate-300 dark:border-zinc-600 bg-transparent p-2"
                          data-testid="portaria-rec-modo">
                          <option value="cable">VB-Cable</option>
                          <option value="loopback">Sem driver (WASAPI)</option>
                        </select>
                      </label>
                      <label className="block text-xs text-zinc-500">Dispositivo de captura
                        <select value={device} onChange={(e) => setDevice(e.target.value)}
                          className="mt-1 w-full text-sm rounded-md border border-slate-300 dark:border-zinc-600 bg-transparent p-2"
                          data-testid="portaria-rec-device">
                          {(modo === "cable" ? devices.entradas : devices.saidas_loopback).map((d) => (
                            <option key={d.index} value={d.index}>{d.nome}</option>
                          ))}
                        </select>
                      </label>
                      <label className="block text-xs text-zinc-500">Microfone do porteiro
                        <select value={mic} onChange={(e) => setMic(e.target.value)}
                          className="mt-1 w-full text-sm rounded-md border border-slate-300 dark:border-zinc-600 bg-transparent p-2"
                          data-testid="portaria-rec-mic">
                          <option value="">Sem microfone</option>
                          {devices.entradas.map((d) => <option key={d.index} value={d.index}>{d.nome}</option>)}
                        </select>
                      </label>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 dark:border-zinc-700 px-4 py-3 space-y-1" data-testid="portaria-php-card">
              <a href="/php/uploadrecordfile.php" download="uploadrecordfile.php"
                className="block text-xs text-blue-500 hover:underline" data-testid="portaria-download-php">
                Baixar uploadrecordfile.php (receptor do servidor de gravações)
              </a>
              <a href="/php/listar.php" download="listar.php"
                className="block text-xs text-blue-500 hover:underline" data-testid="portaria-download-listar-php">
                Baixar listar.php (listagem/stream das gravações — mesma pasta, mesmo token)
              </a>
              <p className="text-[11px] text-zinc-500">
                Instale em /ura/[codigo_tenant]/ no servidor e ajuste o $TOKEN_ESPERADO. No RecordWeb, copie
                config.json.exemplo para config.json com upload_url, tenant e o mesmo token.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
